import './chatMessage.scss';
import React from 'react';
import {
    AiAssistantContextDocument,
    AiAssistantDocumentSource,
    HibuAssistantMessage,
    HibuTagDescription,
    Role
} from './types';
import {ConditionalRender} from '../../common/conditional-render/ConditionalRender';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import Markdown from 'markdown-to-jsx';
import {CopyMessageButton} from './copyMessageButton';
import {useAiAssistantSupportedTags, useAiAssistantWebsearchEnabled} from '../../config/config-hooks';

export type ChatMessageProps = {
    index: number;
    msg: HibuAssistantMessage;
    shouldRenderSources?: boolean;
    hideActions?: boolean;
    isPlaceholder?: boolean;
};

export const ChatMessage = React.memo(function ChatMessage({
                                index,
                                msg,
                                shouldRenderSources = false,
                                hideActions = false,
                                isPlaceholder = false,
                            }: ChatMessageProps): React.JSX.Element {
    const {t} = useTranslation();
    const websearchEnabled: boolean = useAiAssistantWebsearchEnabled();
    const supportedTags: HibuTagDescription[] = useAiAssistantSupportedTags();
    const hasCommandOptions: boolean = websearchEnabled || supportedTags.length > 0;

    const documents: AiAssistantContextDocument[] = msg.metadata?.documents || []; //maybe put this stuff in a useEffect to not render too many times?
    const vectorDocuments = documents.filter((doc) => doc.source !== AiAssistantDocumentSource.WEB);
    const webDocuments = documents.filter((doc) => doc.source === AiAssistantDocumentSource.WEB && doc.url);

    function renderAssistantMessageHeader(index: number) {
        return (
            <div className="assistant-message-title" key={`assistant-title-${index}`}>
                <SvgIcon icon={Icons.AI_ASSISTANT} id="icon-settings" className="icon assistant-title-icon"/>
                <span className="assistant-title-text">{t('assistant.assistant-message-title')}</span>
            </div>
        );
    }

    function renderUserMessageHeader(index: number) {
        return (
            <div className="user-message-title" key={`user-title-${index}`}>
                <span className="user-title-text">{t('assistant.user-message-title')}</span>
                <SvgIcon icon={Icons.USER} id="icon-settings" className="user-title-icon"/>
            </div>
        );
    }

    function renderSources(doc: AiAssistantContextDocument, docIndex: number) {
        if (doc.source === AiAssistantDocumentSource.WEB && doc.url) {
            return (
                <div className="document-source-element" key={`doc-${doc.id}`}>
                    <a href={doc.url} target="_blank" rel="noreferrer" className="document-link">
                        <span className="document-link-title">{docIndex + 1}.</span>
                        <span className="document-link-title">{doc.title}</span>
                    </a>
                </div>
            );
        }

        return (
            <div className="document-source-element" key={`doc-${doc.id}`}>
                <Link to={`/search?f=id%5B${doc.id}%5D&p=1`} target="_blank" className="document-link">
                    <span className="document-link-title">{docIndex + 1}.</span>
                    <span className="document-link-title">{doc.title}</span>
                </Link>
            </div>
        );
    }

    function getCommandPrefix(msg: HibuAssistantMessage): string {
        if (hasCommandOptions) {
            const context = msg.metadata?.context;
            if (msg.role !== Role.USER || !context) {
                return '';
            }

            const prefix = [
                context.enableDocuments ? t('assistant.capabilities.documents.label') : '',
                context.enableWebSearch ? t('assistant.capabilities.web.label') : '',
                context.tags.map((tag: string) => '@' + t(`fieldTitle.tagsPlural.${tag}.label`)).join(' '),
            ]
                .filter(Boolean)
                .join(' ');

            return prefix ? `${prefix}:` : '';
        }
        return '';
    }

    const renderMessage = () => {
        return getCommandPrefix(msg) + ' ' + msg.text;
    };

    function renderOpenAllDocuments(documentsToOpen: AiAssistantContextDocument[]) {
        if (!documentsToOpen.length) {
            return null;
        }

        return (
            <div className="documents-link">
                <Link
                    to={`/search?f=id%5B${encodeURIComponent(
                        documentsToOpen.map((doc: AiAssistantContextDocument) => doc.id).join('|'),
                    )}%5D`}
                    target="_blank"
                    className="document-link"
                >
                    <span className="document-link-title">{t('document-assistant.open-all-documents')}</span>
                </Link>
            </div>
        );
    }

    function renderSourcesSection(title: string, docs: AiAssistantContextDocument[], openAll?: React.ReactNode) {
        if (!docs.length) {
            return null;
        }

        return (
            <div className="sources-group">
                <div className="sources-group-title">{title}</div>
                {docs.map((doc: AiAssistantContextDocument, docIndex: number) => renderSources(doc, docIndex))}
                {openAll}
            </div>
        );
    }

    return (
        <div className="message-wrapper" key={`message-${index}`}>
            <ConditionalRender
                shouldRender={msg.role === Role.ASSISTANT}
                children={renderAssistantMessageHeader(index)}
            />
            <ConditionalRender shouldRender={msg.role === Role.USER} children={renderUserMessageHeader(index)}/>

            <div
                className={`message ${msg.role}${isPlaceholder ? ' generating-answer' : ''}`}
                {...(msg.role === Role.ASSISTANT ? {'data-ai-generated': 'true'} : {})}
            >
                {isPlaceholder ? (
                    <span role="status" aria-label={msg.text}>
                        <span aria-hidden="true">{msg.text.replace(/[.\u2026]+\s*$/, '')}</span>
                        <span className="generating-answer-dots" aria-hidden="true">
                            <span>.</span><span>.</span><span>.</span>
                        </span>
                    </span>
                ) : (
                    <Markdown>{renderMessage()}</Markdown>
                )}
                <ConditionalRender
                    shouldRender={!hideActions && index !== 0 && msg.role === Role.ASSISTANT}
                    children={
                        <>
                            <CopyMessageButton msg={msg.text}/>
                            <ConditionalRender
                                shouldRender={shouldRenderSources}
                                children={
                                    <div className="message-metadata">
                                        <div className="sources-title">{t('document-assistant.sources')}</div>
                                        {renderSourcesSection(
                                            t('document-assistant.document-sources'),
                                            vectorDocuments,
                                            renderOpenAllDocuments(vectorDocuments),
                                        )}
                                        {renderSourcesSection(t('document-assistant.web-sources'), webDocuments)}
                                    </div>
                                }
                            />
                        </>
                    }
                />
            </div>
        </div>
    );
});
